import type { ScoreSizeItem } from "./score-size-buckets";

/** Structural inputs shared by the API DTOs and the web's validated responses. */
export interface ScoreCategoryInput {
	id?: number;
	category: string;
	description: string;
	active?: boolean;
}
export interface ScorePersonaInput extends ScoreCategoryInput {
	jobsToInclude?: string[];
	jobsToExclude?: string[];
	investors: boolean;
	scoreCompanyCategories?: { id?: number }[];
}
export interface ScoreConfigInput {
	serviceDescription?: string | null;
	prospectCompanyCategories?: ScoreCategoryInput[];
	partnerCompanyCategories?: ScoreCategoryInput[];
	prospectPersonaCategories?: ScorePersonaInput[];
	partners: boolean;
	investors: boolean;
	investorConfiguration?: { investors: { name: string; selected: boolean }[] } | null;
}
export interface ScoreCriterionGroupInput<T = unknown> {
	active?: boolean;
	items?: T[];
}
export interface ScoreOtherCriterionInput {
	active?: boolean;
	criteriaName: string;
	criteria: { description: string }[];
}
export interface ScoreRubricInput {
	companyScoring: {
		HQ_COUNTRY: ScoreCriterionGroupInput;
		SUBSIDIARY_COUNTRY: ScoreCriterionGroupInput;
		EMPLOYEE_SIZE: ScoreCriterionGroupInput<ScoreSizeItem>;
		INDUSTRY: ScoreCriterionGroupInput;
		OTHER: ScoreOtherCriterionInput[];
	};
	personaScoring: {
		JOB_LEVEL: ScoreCriterionGroupInput;
		PERSON_LOCATION: ScoreCriterionGroupInput;
		OTHER: ScoreOtherCriterionInput[];
	};
}
export interface ScoreScoringInput {
	id: number;
	name: string;
	locked: boolean;
	scoreCriteriaCompaniesPersonas: {
		id: number;
		scoreCompanyCategoryId?: number | null;
		scorePersonaCategoryId: number;
		investor?: string | null;
	}[];
	criteria: ScoreRubricInput;
}

/**
 * A flat, uniform view of the score configuration (PREC-4055).
 *
 * The API hands segments over as three differently shaped things — prospect and
 * partner categories with ids, and investors as a name list on the score — and
 * the persona ↔ segment link as a nested array on the persona (or a boolean for
 * investors). Every settings page, the overview matrix and the issue engine ask
 * the same questions of that data, so they ask them here, once.
 */

export type SegmentGroup = "PROSPECT" | "PARTNER" | "INVESTOR";

export interface SegmentView {
	/** Stable key across the three shapes: the category id, or `investor|<name>`. */
	key: string;
	id: number | null;
	investorName: string | null;
	name: string;
	description: string;
	group: SegmentGroup;
	active: boolean;
}

export interface PersonaView {
	id: number;
	name: string;
	description: string;
	jobsToInclude: string[];
	jobsToExclude: string[];
	investors: boolean;
	linkedSegmentIds: number[];
	active: boolean;
}

/** The lead scoring a matrix cell currently points at. */
export interface CellAssignment {
	criteriaId: number;
	criteriaName: string;
	locked: boolean;
	ccpId: number;
}

const INVESTOR_PREFIX = "investor|";

export const investorSegmentKey = (investorName: string): string =>
	`${INVESTOR_PREFIX}${investorName}`;

/**
 * The pending-assignment and warning maps key a cell as `<companyId>-<personaId>` or
 * `investor|<name>|<personaId>`; the view keeps that format so the save path and the
 * warning utils keep reading it.
 */
export const cellKey = (segment: SegmentView, personaId: number): string =>
	segment.investorName
		? `${investorSegmentKey(segment.investorName)}|${personaId}`
		: `${segment.id}-${personaId}`;

type Category = ScoreCategoryInput;

const categoryView = (category: Category, group: SegmentGroup): SegmentView | null =>
	category.id === undefined
		? null
		: {
				key: `${category.id}`,
				id: category.id,
				investorName: null,
				name: category.category,
				description: category.description,
				group,
				active: category.active !== false,
			};

/**
 * Every segment the score defines, in matrix order: prospects, partners (only
 * while partners are switched on), then the investor types (only while investors
 * are on). A partner or investor group that is off contributes nothing.
 */
export const segmentsOf = (score: ScoreConfigInput | null | undefined): SegmentView[] => {
	if (!score) {
		return [];
	}

	const prospects = (score.prospectCompanyCategories ?? []).map((category) =>
		categoryView(category, "PROSPECT")
	);
	const partners = score.partners
		? (score.partnerCompanyCategories ?? []).map((category) => categoryView(category, "PARTNER"))
		: [];
	const investors = score.investors
		? (score.investorConfiguration?.investors ?? []).map(
				(investor): SegmentView => ({
					key: investorSegmentKey(investor.name),
					id: null,
					investorName: investor.name,
					name: investor.name,
					description: "",
					group: "INVESTOR",
					active: investor.selected,
				})
			)
		: [];

	return [...prospects, ...partners, ...investors].filter(
		(segment): segment is SegmentView => segment !== null
	);
};

export const personasOf = (score: ScoreConfigInput | null | undefined): PersonaView[] =>
	(score?.prospectPersonaCategories ?? []).flatMap((persona) =>
		persona.id === undefined
			? []
			: [
					{
						id: persona.id,
						name: persona.category,
						description: persona.description,
						jobsToInclude: persona.jobsToInclude ?? [],
						jobsToExclude: persona.jobsToExclude ?? [],
						investors: persona.investors,
						linkedSegmentIds: (persona.scoreCompanyCategories ?? []).flatMap((category) =>
							category.id === undefined ? [] : [category.id]
						),
						active: persona.active !== false,
					},
				]
	);

export const activeSegments = (segments: SegmentView[]): SegmentView[] =>
	segments.filter((segment) => segment.active);

export const activePersonas = (personas: PersonaView[]): PersonaView[] =>
	personas.filter((persona) => persona.active);

/** Whether a persona exists in a segment — for an investor type, whether it applies to investors at all. */
export const isLinked = (persona: PersonaView, segment: SegmentView): boolean =>
	segment.investorName
		? persona.investors
		: segment.id !== null && persona.linkedSegmentIds.includes(segment.id);

const cellMatches = (
	cell: ScoreScoringInput["scoreCriteriaCompaniesPersonas"][number],
	segment: SegmentView,
	personaId: number
): boolean =>
	cell.scorePersonaCategoryId === personaId &&
	(segment.investorName
		? cell.investor === segment.investorName
		: cell.scoreCompanyCategoryId === segment.id);

/** The lead scoring assigned to one cell, or null when the cell has none. */
export const findAssignment = (
	criteria: ScoreScoringInput[],
	segment: SegmentView,
	personaId: number
): CellAssignment | null => {
	for (const scoring of criteria) {
		const cell = scoring.scoreCriteriaCompaniesPersonas.find((candidate) =>
			cellMatches(candidate, segment, personaId)
		);

		if (cell) {
			return {
				criteriaId: scoring.id,
				criteriaName: scoring.name,
				locked: scoring.locked,
				ccpId: cell.id,
			};
		}
	}

	return null;
};

/** How many live cells a lead scoring is applied to. */
export const assignmentCount = (
	scoring: ScoreScoringInput,
	segments: SegmentView[],
	personas: PersonaView[]
): number => {
	let count = 0;

	for (const segment of activeSegments(segments)) {
		for (const persona of activePersonas(personas)) {
			if (
				isLinked(persona, segment) &&
				scoring.scoreCriteriaCompaniesPersonas.some((cell) =>
					cellMatches(cell, segment, persona.id)
				)
			) {
				count += 1;
			}
		}
	}

	return count;
};

/**
 * Whether a rubric criterion takes part in scoring (PREC-4055). The engine
 * grades a criterion only when its flag is set (`criterion?.active &&` in
 * score-lead-scoring.service.ts), so a legacy group that never got a flag is
 * off — the issue engine and the editor must agree on that, or a deactivated
 * criterion is reported as "has no values set".
 */
export const isCriterionGroupActive = (
	group: { active?: boolean | null } | null | undefined
): boolean => group?.active === true;
